import { Button, TextField, WithStyles, Typography, Box } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { CancelOutlined, CloudUpload } from '@material-ui/icons';
import { Autocomplete } from '@mui/material';
import DomainIcon from '@material-ui/icons/Domain';
import FileIcon from '@material-ui/icons/FileCopyOutlined';
import _ from 'lodash';
import React from 'react';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { getDomainNameFromHostname } from '../../repository/utils';
import { IMksImportsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import Permission from '../Permission/Permission';
import ReduxLanguage from '../ReduxLanguage';
import moment from 'moment';

const styles = () => createStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    padding: 10,
    alignItems: 'center',
  },
  filterItem: {
    marginRight: 10,
    minWidth: 180,
  },
});

export interface IImportsFilterBarProps extends WithStyles<typeof styles> {
  onSearch: (params: IMksImportsRequestParamsDTO) => void;
  onResetFilters: () => void;
  onFilterDomains: (value: string) => void;
  onDomainSelect: (domain: IDomainOptionDTO | null) => void;
  onUploadCsv: () => void;
  domainsOptions: IDomainOptionDTO[];
  selectedHostname: string;
  selectedDomain: IDomainOptionDTO | null;
  isVisible?: boolean;
  lastImport?: IMksImportInDTO | {};
}

export interface IImportsFilterBarState {
  domainSearchText: string;
}

class ImportsFilterBar extends React.Component<IImportsFilterBarProps, IImportsFilterBarState> {

  constructor(props: IImportsFilterBarProps) {
    super(props);
    this.state = {
      domainSearchText: this.props.selectedDomain ? this.props.selectedDomain.label : '',
    }
  }

  componentDidUpdate(prevProps: IImportsFilterBarProps) {
    if (prevProps.selectedDomain !== this.props.selectedDomain) {
      const { selectedDomain } = this.props;
      if (selectedDomain) {
        this.setState({ domainSearchText: selectedDomain.label });
      } else {
        this.setState({ domainSearchText: '' });
      }
    }
  }

  public onSearch() {
    const { onSearch, selectedDomain } = this.props;
    
    if (!selectedDomain) return;

    const params: IMksImportsRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: 0,
      pageSize: 100,
    };
    onSearch(params);
  }

  public onResetFilters() {
    const { onResetFilters } = this.props;
    this.setState({ 
      domainSearchText: '',
    });
    onResetFilters();
  }

  public onSelectDomain(domain: IDomainOptionDTO | null) {
    const { onDomainSelect, onResetFilters } = this.props;
    
    if (domain) {
      onDomainSelect(domain);
      this.setState({ domainSearchText: domain.label }, () => {
        this.onSearch();
      });
    } else {
      this.setState({ domainSearchText: '' });
      onResetFilters();
    }
  }

  onFetchDomains(value: string) {
    const { onFilterDomains } = this.props;
    onFilterDomains(value);
  }

  public render() {
    const { classes, domainsOptions, selectedDomain, lastImport } = this.props;
    const { domainSearchText } = this.state;
    const debouncedDomainSearch = _.debounce((text) => this.onFetchDomains(text), 500);
    return (
      <div className={classes.container} style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, width: '100%' }}>
          <div className={classes.filterItem}>
          <Autocomplete
            options={domainsOptions}
            value={selectedDomain || null}
            inputValue={domainSearchText}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.label) return option.label;
              if (option.value?.name) return option.value.name;
              if (option.value?.hostname) return getDomainNameFromHostname(option.value.hostname);
              return '';
            }}
            isOptionEqualToValue={(option, value) => option?.value?.hostname === value?.value?.hostname}
            renderInput={(params) => (
              <TextField 
                {...params} 
                variant="outlined" 
                label={<ReduxLanguage languageKey="mks.hostnameRequired" />}
                required
                error={!selectedDomain}
                helperText={''}
                style={{ minWidth: 250 }}
              />
            )}
            onInputChange={(event, newInputValue) => { 
              this.setState({ domainSearchText: newInputValue }); 
              debouncedDomainSearch(newInputValue); 
            }}
            onChange={(event, newValue) => this.onSelectDomain(newValue)}
            noOptionsText="No domains found"
            loadingText="Loading domains..."
            clearOnBlur={false}
            clearOnEscape={true}
          />
        </div>
        <Button
          variant="contained"
          style={{ 
            backgroundColor: 'red', 
            height: 40, 
            color: 'white', 
            fontWeight: 'bold',
            minWidth: 'auto',
            padding: '8px 16px'
          }}
          onClick={() => this.onResetFilters()}
        >
          <CancelOutlined style={{ color: 'white' }} />
        </Button>

        <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.MKS_CSV_IMPORT}>
          {selectedDomain && (
            <Button
              variant="contained"
              style={{ backgroundColor: 'cornflowerblue', color: 'white', marginRight: 20, marginLeft: 20, fontWeight: 'bold'  }}
              onClick={() => this.props.onUploadCsv()}
            >
              <CloudUpload  style={{ marginRight: 10 }}/>
              <ReduxLanguage languageKey="mks.uploadCsv" />
            </Button>
          )}
        </Permission>
        {lastImport && Object.keys(lastImport).length > 0 && (
          <Box style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingRight: '10px' }}>
            <Typography variant="body2" style={{ fontWeight: 'bold', color: '#666', fontSize: 17 }}>
              <ReduxLanguage languageKey="mks.currentConfig" />
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <DomainIcon style={{ marginRight: 5, color: '#333', fontSize: 16 }} />
              <Typography variant="body2" style={{ color: '#333' }}>
                {(getDomainNameFromHostname(selectedDomain?.value?.hostname))}
              </Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileIcon style={{ marginRight: 5, color: '#333', fontSize: 16 }} />
            <Typography variant="body2" style={{ color: '#333' }}>
              {(lastImport as IMksImportInDTO).filename}
            </Typography>
            </div>
            <Typography variant="caption" style={{ color: '#999' }}>
              {moment((lastImport as IMksImportInDTO).importTimestamp).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
        )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(ImportsFilterBar);
